import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { toast } from "react-hot-toast";
import { Table, Badge, Modal } from "react-bootstrap";
import { Icon } from "@iconify/react";
import {
  BillService,
  getCurrentTerm,
  AdjustmentService,
} from "../../services/billService";
import { ConfirmModal } from "../../components/custom/ConfirmModal";
import { AllClasses } from "../../configs/allClasses";
import { useCurrentTermAndSession } from "../../hooks/useCurrentTermAndSession";

// Resolve class code → human readable name
const getClassName = (code) => {
  const found = AllClasses.find((c) => c.code === code);
  return found ? found.name : code || "—";
};

// Derive academic session from a JS Date (e.g. Aug 2024 → "2024/2025")
const deriveSession = (date) => {
  const d = date ? new Date(date) : new Date();
  const year = d.getFullYear();
  const month = d.getMonth() + 1; // 1-indexed
  return month >= 8 ? `${year}/${year + 1}` : `${year - 1}/${year}`;
};

const STATUS_COLORS = {
  draft: "secondary",
  issued: "primary",
  partial: "warning",
  paid: "success",
};

export function GenerateBills() {
  const { currentSession, currentTerm, isLoading: hookLoading } = useCurrentTermAndSession();
  const [isInitialized, setIsInitialized] = useState(false);

  const [session, setSession] = useState("");
  const [term, setTerm] = useState("");
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [termLoading, setTermLoading] = useState(true);

  useEffect(() => {
    if (currentSession && currentTerm && !isInitialized) {
      setSession(currentSession);
      setTerm(currentTerm);
      setIsInitialized(true);
      setTermLoading(false);
    } else if (!hookLoading && !isInitialized) {
      setTermLoading(false);
      setIsInitialized(true);
    }
  }, [currentSession, currentTerm, hookLoading, isInitialized]);

  // Detail modal state
  const [detailModal, setDetailModal] = useState({ show: false, bill: null });
  const [adjustments, setAdjustments] = useState([]);
  const [adjForm, setAdjForm] = useState({
    show: false,
    type: "credit",
    amount: "",
    description: "",
  });
  const [adjLoading, setAdjLoading] = useState(false);

  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: "",
    message: "",
    onConfirm: null,
    variant: "primary",
  });

  // ── On mount: auto-load current term from the backend (Replaced by hook) ──────────────────────

  useEffect(() => {
    if (isInitialized && session && term) fetchBills();
  }, [session, term, isInitialized]);

  const fetchBills = async () => {
    try {
      const res = await BillService.getBills({ session, term });
      setBills(res.data);
    } catch (error) {
      console.error("Error fetching bills", error);
    }
  };

  const handleViewDetail = async (bill) => {
    try {
      const [billRes, adjRes] = await Promise.all([
        BillService.getBillById(bill._id),
        AdjustmentService.getByBill(bill._id),
      ]);
      setDetailModal({ show: true, bill: billRes.data });
      setAdjustments(adjRes.data || []);
      setAdjForm({ show: false, type: "credit", amount: "", description: "" });
    } catch {
      // Fallback
      setDetailModal({ show: true, bill });
      setAdjustments([]);
    }
  };

  const handleApplyAdjustment = async (e) => {
    e.preventDefault();
    if (!adjForm.amount || !adjForm.description)
      return toast.error("Please fill all fields");
    setAdjLoading(true);
    try {
      const res = await AdjustmentService.apply(activeBill._id, {
        type: adjForm.type,
        amount: Number(adjForm.amount),
        description: adjForm.description,
      });
      // the endpoint returns the updated bill summary
      setDetailModal((prev) => ({
        ...prev,
        bill: { ...prev.bill, ...res.data.updatedBill },
      }));
      setAdjustments([res.data.adjustment, ...adjustments]);
      setAdjForm({ show: false, type: "credit", amount: "", description: "" });
      toast.success(`${adjForm.type} applied successfully`);
      fetchBills(); // background sync the list
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to apply adjustment");
    } finally {
      setAdjLoading(false);
    }
  };

  const handleDeleteAdjustment = async (adjId) => {
    if (
      !window.confirm(
        "Delete this adjustment? It will reverse its effect on the bill.",
      )
    )
      return;
    try {
      await AdjustmentService.remove(adjId);
      // Re-fetch the whole bill and its adjustments to reliably sync state
      const [billRes, adjRes] = await Promise.all([
        BillService.getBillById(activeBill._id),
        AdjustmentService.getByBill(activeBill._id),
      ]);
      setDetailModal({ show: true, bill: billRes.data });
      setAdjustments(adjRes.data || []);
      toast.success("Adjustment reversed");
      fetchBills();
    } catch (err) {
      toast.error("Failed to reverse adjustment");
    }
  };

  const handleGenerate = () => {
    setConfirmModal({
      show: true,
      title: "Generate Bills",
      message: `Generate term bills for all active students for ${term} — ${session}?`,
      variant: "primary",
      onConfirm: async () => {
        setConfirmModal((prev) => ({ ...prev, show: false }));
        setLoading(true);
        try {
          const res = await BillService.generateBills({ session, term });
          const { generated, skipped } = res.data;
          if (generated > 0) {
            toast.success(
              `Successfully generated ${generated} draft bill${generated > 1 ? "s" : ""}.`,
            );
          } else {
            toast.error(
              "No new bills generated. Check that class presets are set up.",
            );
          }
          if (skipped > 0) {
            toast(`${skipped} student(s) skipped — missing class presets.`, {
              icon: "⚠️",
            });
          }
          fetchBills();
        } catch (error) {
          toast.error(
            error?.response?.data?.message || "Failed to generate bills",
          );
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleIssueBills = () => {
    setConfirmModal({
      show: true,
      title: "Publish Bills",
      message:
        "Are you sure? This publishes all drafts to the Parent Portal. Parents will be able to see them immediately.",
      variant: "success",
      onConfirm: async () => {
        setConfirmModal((prev) => ({ ...prev, show: false }));
        setLoading(true);
        try {
          await BillService.issueBills({ session, term });
          toast.success("All draft bills have been published!");
          fetchBills();
        } catch {
          toast.error("Failed to publish bills");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleDeleteDrafts = () => {
    setConfirmModal({
      show: true,
      title: "Delete Drafts",
      message:
        "DANGER! Delete all DRAFT bills for this term? You will need to regenerate them.",
      variant: "danger",
      onConfirm: async () => {
        setConfirmModal((prev) => ({ ...prev, show: false }));
        try {
          await BillService.deleteDrafts({ session, term });
          toast.success("Draft bills deleted.");
          fetchBills();
        } catch {
          toast.error("Failed to delete drafts");
        }
      },
    });
  };

  const draftCount = bills.filter((b) => b.status === "draft").length;
  const issuedCount = bills.filter((b) => b.status !== "draft").length;
  const totalRevenue = bills
    .filter((b) => b.status !== "draft")
    .reduce((sum, b) => sum + b.amountPaid, 0);
  const outstandingBalance = bills
    .filter((b) => b.status !== "draft")
    .reduce((sum, b) => sum + b.balance, 0);

  const activeBill = detailModal.bill;

  return (
    <Container>
      <ConfirmModal
        show={confirmModal.show}
        onClose={() => setConfirmModal((prev) => ({ ...prev, show: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        confirmText="Confirm"
      />

      <Header>
        <div>
          <h3>Generate &amp; Manage Bills</h3>
          <p>Auto-generate term bills and publish them to the Parent Portal.</p>
        </div>
        {currentTerm && (
          <div style={{ marginBottom: "15px", color: "#666" }}>
            <Badge bg="info" className="me-2">
              Active Term: <strong>{currentTerm}</strong> &nbsp;|&nbsp;{" "}
              Session: <strong>{currentSession}</strong>
            </Badge>
          </div>
        )}
      </Header>

      <Controls>
        <SelectGroup>
          <label>Academic Session</label>
          <select value={session} onChange={(e) => setSession(e.target.value)}>
            <option value="2023/2024">2023/2024</option>
            <option value="2024/2025">2024/2025</option>
            <option value="2025/2026">2025/2026</option>
            <option value="2026/2027">2026/2027</option>
            <option value="2027/2028">2027/2028</option>
            <option value="2028/2029">2028/2029</option>

          </select>
        </SelectGroup>

        <SelectGroup>
          <label>Term</label>
          <select value={term} onChange={(e) => setTerm(e.target.value)}>
            <option value="FIRST TERM">FIRST TERM</option>
            <option value="SECOND TERM">SECOND TERM</option>
            <option value="THIRD TERM">THIRD TERM</option>
          </select>
        </SelectGroup>

        <ActionButtons>
          <PrimaryButton onClick={handleGenerate} disabled={loading}>
            <Icon icon="mdi:file-document-plus" width="18" />
            Generate Draft Bills
          </PrimaryButton>

          {draftCount > 0 && (
            <>
              <SuccessButton onClick={handleIssueBills} disabled={loading}>
                <Icon icon="mdi:publish" width="18" />
                Publish {draftCount} Draft{draftCount > 1 ? "s" : ""}
              </SuccessButton>
              <DangerButton onClick={handleDeleteDrafts}>
                <Icon icon="mdi:trash-can" width="18" />
                Clear Drafts
              </DangerButton>
            </>
          )}
        </ActionButtons>
      </Controls>

      {/* Stats bar */}
      <StatsBar>
        <StatCard>
          <StatLabel>Total Bills</StatLabel>
          <StatValue>{bills.length}</StatValue>
        </StatCard>
        <StatCard color="#856404" bg="#fff3cd">
          <StatLabel>Drafts</StatLabel>
          <StatValue>{draftCount}</StatValue>
        </StatCard>
        <StatCard color="#155724" bg="#d4edda">
          <StatLabel>Issued</StatLabel>
          <StatValue>{issuedCount}</StatValue>
        </StatCard>
        {/* <StatCard color="#155724" bg="#d4edda">
          <StatLabel>Revenue Collected</StatLabel>
          <StatValue>₦{totalRevenue.toLocaleString()}</StatValue>
        </StatCard> */}
        {/* <StatCard color="#721c24" bg="#f8d7da">
          <StatLabel>Outstanding Balance</StatLabel>
          <StatValue>₦{outstandingBalance.toLocaleString()}</StatValue>
        </StatCard> */}
      </StatsBar>

      <Card>
        {bills.length === 0 ? (
          <EmptyState>
            <Icon icon="mdi:file-document-outline" width="48" color="#ccc" />
            <p>
              No bills found for{" "}
              <strong>
                {term} — {session}
              </strong>
              .
            </p>
            <small>Click "Generate Draft Bills" to begin.</small>
          </EmptyState>
        ) : (
          <Table responsive hover>
            <thead>
              <tr>
                <th>Student</th>
                <th>Class</th>
                <th>Total (₦)</th>
                <th>Paid (₦)</th>
                <th>Balance (₦)</th>
                <th>Status</th>
                <th>View</th>
              </tr>
            </thead>
            <tbody>
              {bills.map((b) => (
                <tr key={b._id}>
                  <td>
                    <strong>
                      {b.studentId?.firstName} {b.studentId?.lastName}
                    </strong>
                    <br />
                    <small style={{ color: "#888" }}>
                      {b.studentId?.admissionNumber}
                    </small>
                  </td>
                  <td>{getClassName(b.studentId?.currentClass)}</td>
                  <td>₦{b.totalAmount.toLocaleString()}</td>
                  <td style={{ color: "#28a745", fontWeight: 600 }}>
                    ₦{b.amountPaid.toLocaleString()}
                  </td>
                  <td
                    style={{
                      color: b.balance > 0 ? "#dc3545" : "#28a745",
                      fontWeight: 700,
                    }}
                  >
                    ₦{b.balance.toLocaleString()}
                  </td>
                  <td>
                    <Badge bg={STATUS_COLORS[b.status] || "secondary"}>
                      {b.status.toUpperCase()}
                    </Badge>
                  </td>
                  <td>
                    <ActionBtn
                      onClick={() => handleViewDetail(b)}
                      title="View bill details"
                    >
                      <Icon icon="mdi:eye" color="#1a73e8" width="22" />
                    </ActionBtn>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {/* ── Detailed Bill View Modal ────────────────────────────────────────── */}
      <Modal
        show={detailModal.show}
        onHide={() => setDetailModal({ show: false, bill: null })}
        size="lg"
        centered
      >
        {activeBill && (
          <>
            <Modal.Header closeButton style={{ background: "#f8f9fa" }}>
              <Modal.Title>
                <Icon
                  icon="mdi:receipt"
                  width="22"
                  style={{ marginRight: 8, verticalAlign: "middle" }}
                />
                Bill Details
              </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: "0" }}>
              {/* Student info block */}
              <StudentInfoBlock>
                <InfoRow>
                  <InfoLabel>Student</InfoLabel>
                  <InfoValue>
                    <strong>
                      {activeBill.studentId?.firstName}{" "}
                      {activeBill.studentId?.middleName}{" "}
                      {activeBill.studentId?.lastName}
                    </strong>
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Admission No.</InfoLabel>
                  <InfoValue>{activeBill.studentId?.admissionNumber}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Class</InfoLabel>
                  <InfoValue>
                    {getClassName(activeBill.studentId?.currentClass)}
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Session / Term</InfoLabel>
                  <InfoValue>
                    {activeBill.session} — {activeBill.term}
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Status</InfoLabel>
                  <InfoValue>
                    <Badge
                      bg={STATUS_COLORS[activeBill.status] || "secondary"}
                      style={{ fontSize: 13 }}
                    >
                      {activeBill.status.toUpperCase()}
                    </Badge>
                  </InfoValue>
                </InfoRow>
                {activeBill.generatedBy && (
                  <InfoRow>
                    <InfoLabel>Generated By</InfoLabel>
                    <InfoValue>
                      {activeBill.generatedBy?.firstName}{" "}
                      {activeBill.generatedBy?.lastName}
                    </InfoValue>
                  </InfoRow>
                )}
              </StudentInfoBlock>

              {/* Fee breakdown table */}
              <BreakdownSection>
                <SectionTitle>Fee Breakdown</SectionTitle>
                <Table size="sm" bordered>
                  <thead style={{ background: "#f1f3f4" }}>
                    <tr>
                      <th>#</th>
                      <th>Description</th>
                      <th style={{ textAlign: "right" }}>Amount (₦)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(activeBill.breakdown || []).map((item, idx) => (
                      <tr key={idx}>
                        <td style={{ color: "#888", fontSize: 13 }}>
                          {idx + 1}
                        </td>
                        <td>{item.description}</td>
                        <td style={{ textAlign: "right", fontWeight: 600 }}>
                          ₦{item.amount.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </BreakdownSection>

              {/* ── Adjustments Section (Debit Advice) ── */}
              <AdjustmentsSection>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 12,
                  }}
                >
                  <SectionTitle style={{ marginBottom: 0 }}>
                    Adjustments (Debit/Credit Advice)
                  </SectionTitle>
                  {!adjForm.show && (
                    <AddAdjBtn
                      onClick={() => setAdjForm({ ...adjForm, show: true })}
                    >
                      <Icon icon="mdi:plus" /> Add Adjustment
                    </AddAdjBtn>
                  )}
                </div>

                {adjForm.show && (
                  <AdjForm onSubmit={handleApplyAdjustment}>
                    <div className="fields">
                      <select
                        value={adjForm.type}
                        onChange={(e) =>
                          setAdjForm({ ...adjForm, type: e.target.value })
                        }
                      >
                        <option value="credit">
                          Credit (Deduct from Bill)
                        </option>
                        <option value="debit">Debit (Add to Bill)</option>
                      </select>
                      <input
                        type="text"
                        placeholder="Description (e.g. Scholarship)"
                        value={adjForm.description}
                        onChange={(e) =>
                          setAdjForm({
                            ...adjForm,
                            description: e.target.value,
                          })
                        }
                      />
                      <input
                        type="number"
                        placeholder="Amount (₦)"
                        min="1"
                        value={adjForm.amount}
                        onChange={(e) =>
                          setAdjForm({ ...adjForm, amount: e.target.value })
                        }
                      />
                    </div>
                    <div className="actions">
                      <SuccessButton
                        type="submit"
                        disabled={adjLoading}
                        style={{ padding: "6px 12px", fontSize: 13 }}
                      >
                        {adjLoading ? "Saving..." : "Apply"}
                      </SuccessButton>
                      <CancelBtn
                        type="button"
                        onClick={() => setAdjForm({ ...adjForm, show: false })}
                      >
                        Cancel
                      </CancelBtn>
                    </div>
                  </AdjForm>
                )}

                {adjustments.length > 0 ? (
                  <Table size="sm" style={{ fontSize: 13 }}>
                    <tbody>
                      {adjustments.map((adj) => (
                        <tr key={adj._id}>
                          <td style={{ width: 80 }}>
                            <Badge
                              bg={adj.type === "credit" ? "success" : "danger"}
                            >
                              {adj.type.toUpperCase()}
                            </Badge>
                          </td>
                          <td>
                            {adj.description} <br />
                            <small style={{ color: "#888" }}>
                              By {adj.appliedBy?.firstName}{" "}
                              {adj.appliedBy?.lastName} on{" "}
                              {new Date(adj.createdAt).toLocaleDateString()}
                            </small>
                          </td>
                          <td
                            style={{
                              textAlign: "right",
                              fontWeight: 600,
                              color:
                                adj.type === "credit" ? "#28a745" : "#dc3545",
                            }}
                          >
                            {adj.type === "credit" ? "-" : "+"} ₦
                            {adj.amount.toLocaleString()}
                          </td>
                          <td style={{ width: 40, textAlign: "right" }}>
                            <ActionBtn
                              onClick={() => handleDeleteAdjustment(adj._id)}
                              title="Delete & Reverse"
                            >
                              <Icon
                                icon="mdi:trash-can"
                                color="#dc3545"
                                width="16"
                              />
                            </ActionBtn>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <NoBillMsg style={{ padding: 10 }}>
                    No adjustments applied.
                  </NoBillMsg>
                )}
              </AdjustmentsSection>

              {/* Payment summary */}
              <PaymentSummary>
                <PayRow>
                  <span>Total Bill:</span>
                  <strong>₦{activeBill.totalAmount.toLocaleString()}</strong>
                </PayRow>
                <PayRow style={{ color: "#28a745" }}>
                  <span>Amount Paid:</span>
                  <strong>₦{activeBill.amountPaid.toLocaleString()}</strong>
                </PayRow>
                <PayRow
                  style={{
                    color: activeBill.balance > 0 ? "#dc3545" : "#28a745",
                    borderTop: "1px solid #eee",
                    paddingTop: 10,
                    marginTop: 6,
                  }}
                >
                  <span>Outstanding Balance:</span>
                  <strong style={{ fontSize: 18 }}>
                    ₦{activeBill.balance.toLocaleString()}
                  </strong>
                </PayRow>
              </PaymentSummary>
            </Modal.Body>
          </>
        )}
      </Modal>
    </Container>
  );
}

// ─── Styled Components ─────────────────────────────────────────────────────────

const Container = styled.div`
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 25px;
  h3 {
    margin: 0;
    color: #333;
  }
  p {
    color: #666;
    margin-top: 5px;
  }
`;

const CurrentTermBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #e8f0fe;
  color: #1a73e8;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  align-self: center;
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 20px;
  margin-bottom: 20px;
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const SelectGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  label {
    font-size: 14px;
    font-weight: 500;
    color: #444;
  }
  select {
    padding: 10px 15px;
    border-radius: 6px;
    border: 1px solid #ddd;
    min-width: 200px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
  margin-left: auto;
  flex-wrap: wrap;
`;

const BaseButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.85;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(BaseButton)`
  background: #1a73e8;
`;
const SuccessButton = styled(BaseButton)`
  background: #28a745;
`;
const DangerButton = styled(BaseButton)`
  background: #dc3545;
`;

const StatsBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 20px;
`;

const StatCard = styled.div`
  background: ${(p) => p.bg || "white"};
  color: ${(p) => p.color || "#333"};
  padding: 15px 25px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
  min-width: 140px;
`;

const StatLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.8;
`;

const StatValue = styled.div`
  font-size: 22px;
  font-weight: 700;
  margin-top: 4px;
`;

const Card = styled.div`
  background: white;
  padding: 25px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const EmptyState = styled.div`
  padding: 50px 40px;
  text-align: center;
  color: #777;
  p {
    margin: 10px 0 4px;
    font-size: 16px;
  }
  small {
    color: #aaa;
  }
`;

const ActionBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  &:hover {
    opacity: 0.7;
  }
`;

/* Modal inner sections */
const StudentInfoBlock = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #eee;
  background: #fafafa;
`;

const InfoRow = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 8px;
  font-size: 14px;
`;

const InfoLabel = styled.div`
  color: #888;
  min-width: 130px;
  font-weight: 500;
`;

const InfoValue = styled.div`
  color: #222;
`;

const BreakdownSection = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #eee;
`;

const SectionTitle = styled.h6`
  font-weight: 700;
  color: #444;
  margin-bottom: 12px;
  letter-spacing: 0.3px;
`;

const PaymentSummary = styled.div`
  padding: 20px 24px;
`;

const PayRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 15px;
  margin-bottom: 8px;
`;

const AdjustmentsSection = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #eee;
  background: #fdfdfd;
`;

const AddAdjBtn = styled.button`
  background: none;
  border: 1px dashed #1a73e8;
  color: #1a73e8;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  &:hover {
    background: #f0f4ff;
  }
`;

const AdjForm = styled.form`
  background: #f8f9fa;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 15px;
  border: 1px solid #e9ecef;

  .fields {
    display: flex;
    gap: 10px;
    margin-bottom: 10px;

    select,
    input {
      padding: 6px 10px;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-size: 13px;
      outline: none;
    }
    input[type="text"] {
      flex: 2;
    }
    input[type="number"] {
      flex: 1;
    }
    select {
      flex: 1;
    }
  }

  .actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }
`;

const CancelBtn = styled.button`
  background: none;
  border: none;
  font-size: 13px;
  color: #666;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const NoBillMsg = styled.div`
  color: #aaa;
  font-size: 13px;
  font-style: italic;
`;
